import { useEffect, useRef, useState } from "react";
import { useView, type ViewName } from "../view/useView";

/** One view's three files, already fetched. */
export interface ViewBundle {
  styleText: string;
  bodyHtml: string;
  scriptText: string;
}

/** How a view's three files are fetched. Async so Vite can code-split them. */
export type ViewLoader = () => Promise<ViewBundle>;

export interface PageShellProps {
  /** Original <title> text for this page. */
  title: string;
  /** Original <meta name="description"> content for this page. */
  description: string;
  /** Original <body class="..."> value, or "" if the page had no class. */
  bodyClass?: string;
  /** Original <html data-frame="..."> value, or null if the page didn't set it. */
  dataFrame?: string | null;
  /** The desktop and mobile builds of this page. */
  views: Record<ViewName, ViewLoader>;
}

/**
 * Mounts one of a page's two views - desktop or mobile - and swaps between
 * them when the viewport crosses the breakpoint.
 *
 * Each view is a self-contained trio of markup, stylesheet and script, and
 * only one of them is ever in the document. That is the point of the split:
 * neither view has to defend itself against the other's rules, so there is no
 * cascade to out-specify and no `!important` standing between a stylesheet
 * and what it is trying to say. A view's script can assume its own markup
 * exists and can stop asking how wide the window is.
 *
 * The two views are loaded on demand rather than imported outright, so a
 * phone never downloads the desktop build (which for /insights is over 400KB
 * of script on its own) and a desktop never downloads the mobile one.
 *
 * On a swap the previous view is torn down completely - its <style> and
 * <script> tags removed, its markup dropped - before the next is mounted, and
 * document.title / the description meta / body's class / <html data-frame>
 * are restored, so nothing about one view leaks into the other.
 *
 * Known trade-off, inherited from the original static pages: the scripts were
 * written for a world where navigating away always meant a full reload, so
 * they do not remove their own window/document listeners. Dropping the markup
 * they touch makes those listeners no-ops rather than a visible bug. A view
 * swap now makes that happen more often than a route change used to, so any
 * NEW listener a view adds should be registered through the `onTeardown`
 * callback the scripts are handed (see below) rather than left to leak.
 */
export default function PageShell({
  title,
  description,
  bodyClass = "",
  dataFrame = null,
  views,
}: PageShellProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  // carried across a teardown/mount pair so a swap can restore the position
  const swapRatio = useRef<number | null>(null);
  const view = useView();
  const [bundle, setBundle] = useState<{ view: ViewName; files: ViewBundle } | null>(
    null
  );

  // --- fetch the view's three files (cached by the module loader) ---------
  useEffect(() => {
    let live = true;
    views[view]().then((files) => {
      if (live) setBundle({ view, files });
    });
    return () => {
      live = false;
    };
  }, [view, views]);

  // --- mount it ----------------------------------------------------------
  useEffect(() => {
    if (!bundle || bundle.view !== view) return;
    const { styleText, bodyHtml, scriptText } = bundle.files;
    const htmlEl = document.documentElement;

    // --- remember what was here before, so we can restore it on cleanup ---
    const prevTitle = document.title;
    const prevBodyClass = document.body.className;
    const prevDataFrame = htmlEl.getAttribute("data-frame");

    let metaEl = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]'
    );
    if (!metaEl) {
      metaEl = document.createElement("meta");
      metaEl.setAttribute("name", "description");
      document.head.appendChild(metaEl);
    }
    const prevDescription = metaEl.getAttribute("content");

    // --- apply this view's head-level state -------------------------------
    document.title = title;
    metaEl.setAttribute("content", description);
    document.body.className = bodyClass;
    if (dataFrame) htmlEl.setAttribute("data-frame", dataFrame);
    else htmlEl.removeAttribute("data-frame");
    htmlEl.setAttribute("data-view", view);

    // --- inject this view's stylesheet ------------------------------------
    const styleEl = document.createElement("style");
    styleEl.textContent = styleText;
    document.head.appendChild(styleEl);

    // --- inject this view's markup ----------------------------------------
    if (rootRef.current) rootRef.current.innerHTML = bodyHtml;

    // --- run this view's script -------------------------------------------
    // (assigning via innerHTML never runs <script> tags, so this is recreated
    // as a real script element the same way the browser would have run it)
    //
    // The script is wrapped so it can register its own cleanup: anything it
    // passes to window.__twTeardown is called when the view is torn down.
    // That is what lets a view add window listeners and intervals without
    // leaking them across a swap.
    const teardowns: Array<() => void> = [];
    (window as unknown as { __twTeardown?: (fn: () => void) => void }).__twTeardown =
      (fn) => teardowns.push(fn);

    const scriptEl = document.createElement("script");
    scriptEl.textContent = scriptText;
    document.body.appendChild(scriptEl);

    // A fresh view starts at the top, but a swap should not throw the reader
    // back to the top of a page they were halfway down. The two layouts are
    // different heights, so the nearest honest equivalent is the same
    // fraction of the document.
    const swapped = swapRatio.current;
    swapRatio.current = null;
    requestAnimationFrame(() => {
      if (swapped !== null) {
        const h = document.documentElement.scrollHeight - window.innerHeight;
        window.scrollTo(0, Math.max(0, Math.round(swapped * h)));
      } else if (!window.location.hash) {
        window.scrollTo(0, 0);
      }
    });

    return () => {
      // remember where we were, in case this teardown is a view swap
      const h = document.documentElement.scrollHeight - window.innerHeight;
      swapRatio.current = h > 0 ? window.scrollY / h : 0;

      for (const fn of teardowns) {
        try {
          fn();
        } catch {
          /* a view's own cleanup must never block the swap */
        }
      }
      delete (window as unknown as { __twTeardown?: unknown }).__twTeardown;

      styleEl.remove();
      scriptEl.remove();
      if (rootRef.current) rootRef.current.innerHTML = "";
      document.title = prevTitle;
      document.body.className = prevBodyClass;
      htmlEl.removeAttribute("data-view");
      if (prevDataFrame) htmlEl.setAttribute("data-frame", prevDataFrame);
      else htmlEl.removeAttribute("data-frame");
      if (metaEl && prevDescription !== null) {
        metaEl.setAttribute("content", prevDescription);
      }
    };
  }, [bundle, view, title, description, bodyClass, dataFrame]);

  return <div ref={rootRef} />;
}
